import React from 'react';
import { Link } from 'react-router-dom';

const StartYourJourneySection = () => (
  <div className="py-16 bg-gray-50 dark:bg-gray-800">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
          Start Your Journey to Energy Productivity
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-4xl mx-auto">
          Choose your path: Start with a free assessment or get comprehensive professional guidance.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
        {/* Self Guided Journey Card */}
        <div className="bg-white dark:bg-gray-900 border-2 border-emerald-300 dark:border-emerald-600 rounded-2xl p-8 relative">
          <div className="mb-6">
            <div className="bg-emerald-600 dark:bg-emerald-700 text-white px-4 py-2 rounded-full text-sm font-bold inline-block mb-4">
              FREE ASSESSMENT
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Self Guided Journey
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Take control of your energy efficiency journey with our comprehensive self-assessment
              tools.
            </p>
          </div>

          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              What You Get:
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  Comprehensive energy assessment questionnaire
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  Instant results and savings calculations
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  Priority-ranked energy saving opportunities
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  Implementation guidance and best practices
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  Industry-specific recommendations
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  Resource links and vendor connections
                </span>
              </li>
            </ul>
          </div>

          <Link
            to="/assessment/self-guided"
            className="w-full bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white px-6 py-4 rounded-lg font-semibold transition-colors inline-flex items-center justify-center gap-2"
          >
            Start Free Assessment
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {/* Zurich Guided Journey Card */}
        <div className="bg-white dark:bg-gray-900 border-2 border-blue-300 dark:border-blue-600 rounded-2xl p-8 relative">
          <div className="mb-6">
            <div className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-bold inline-block mb-4">
              PROFESSIONAL SERVICE
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Zurich Guided Journey
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Get expert guidance with our comprehensive professional assessment and implementation
              support.
            </p>
          </div>

          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              What You Get:
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  Dedicated energy consultant assigned to your project
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  On-site facility assessment and audit
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  Detailed financial analysis and ROI projections
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  Implementation roadmap with timeline
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  Access to pre-vetted implementation partners
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  Ongoing support and progress monitoring
                </span>
              </li>
            </ul>
          </div>

          <Link
            to="/login"
            className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white px-6 py-4 rounded-lg font-semibold transition-colors inline-flex items-center justify-center gap-2"
          >
            Book Professional Assessment
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  </div>
);

export default StartYourJourneySection;


